import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, IconButton, Modal, Switch, TextField, Typography } from '@material-ui/core'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { Delete as DeleteIcon } from '@material-ui/icons'
import esLocale from 'date-fns/locale/es'
import { Conversation, ExtraField, Role, Stages, TrackingRows } from '@types'
import { format } from 'date-fns'
import { userProfile } from '@views/profile/state/profileSlice'
import { useDispatch, useSelector } from 'react-redux'
import { postNewConversation } from '../state/trackingSlice2'

interface Props {
  isOpen: boolean
  selectedRow?: TrackingRows
  onSave: (
    stages: Stages,
    extraFields: ExtraField[],
    isRejected: boolean,
    isReturned: boolean,
    successForm: SuccessForm,
    hasChangedSuccess: boolean
  ) => void
  onCancel: () => void
  onSuccess: () => void
}

interface DateStages {
  studyDate: Date | null
  office: Date | null
  bank: Date | null
  success: Date | null
  fee?: number
  payments?: number
  rejected?: Date | null
  returned?: Date | null
  delivered?: Date | null
  received?: Date | null
}

export interface SuccessForm {
  companyDocs: string
  email: string
  creditNumber: number | null
  newInstallments: number | null
  savedInstallments: number | null
  savedTaxes: number | null
  newTotalAmount: number | null
  observations?: string | null
}

const emptyStages: DateStages = {
  studyDate: null,
  office: null,
  bank: null,
  success: null,
  rejected: null,
  returned: null,
  delivered: null,
  received: null
}

const emptySuccessForm: SuccessForm = {
  email: '',
  companyDocs: '',
  creditNumber: null,
  newInstallments: null,
  savedInstallments: null,
  savedTaxes: null,
  newTotalAmount: null,
  observations: null
}

const extraFieldsBank: { [key: string]: ExtraField[] } = {
  FNA: [
    { name: 'Radicado Fase 1', value: '', type: 'date' },
    { name: 'Número de Radicado', value: '', type: 'text' },
    { name: 'Aprobado Fase 1', value: '', type: 'date' },
    { name: 'Firmado Fase 2', value: '', type: 'date' }
  ],
  'AV VILLAS': [],
  'BANCO DE BOGOTA': [],
  'BANCO DE OCCIDENTE': [],
  BANCOLOMBIA: [
    { name: 'Radicado en Banco', value: '', type: 'date' },
    { name: 'Número de Radicado', value: '', type: 'text' },
    { name: 'Aprobado', value: '', type: 'date' }
  ],
  BANCOOMEVA: [],
  'CAJA SOCIAL': [{ name: 'Firma de otro si', value: '', type: 'date' }],
  DAVIVIENDA: [
    { name: 'Número de Radicado', value: '', type: 'text' },
    { name: 'Fecha envío Documentos PDF', value: '', type: 'date' },
    { name: 'Fecha de confirmación nuevos valores', value: '', type: 'date' },
    { name: 'Fecha Label', value: '', type: 'date' }
  ],
  ITAU: [],
  'LA HIPOTECARIA': [],
  POPULAR: [],
  'SCOTIABANK COLPATRIA': []
}

const numRegex = /^[0-9\b]+$/

const EditStagesModal: React.FC<Props> = ({ isOpen, selectedRow, onSave, onCancel, onSuccess }) => {
  if (!selectedRow) return null

  const { role } = useSelector(userProfile)
  const [selectedStages, setSelectedStages] = useState(emptyStages)
  const [newConversation, setNewConversation] = useState('')
  const [successForm, setSuccessForm] = useState<SuccessForm>(emptySuccessForm)
  const [conversation, setConversation] = useState<Conversation[]>([])
  const [isRejectedState, setIsRejectedState] = useState(!!selectedRow?.isRejected)
  const [isReturnedState, setIsReturnedState] = useState(!!selectedRow?.isReturned)
  const [hasChangedSuccess, setHasChangeSuccess] = useState(false)
  const [hasChangedOffice, setHasChangeOffice] = useState(false)
  const [hasChangedBank, setHasChangeBank] = useState(false)
  const [isObservation, setIsObservation] = useState(selectedRow?.isObservation)
  const [isTest, setIsTest] = useState(selectedRow?.isTest)
  const formValid = successForm.companyDocs && successForm.creditNumber && successForm.newInstallments
  const [extraForm, setExtraForm] = useState<ExtraField[]>([])
  const extraEmptyForm = extraFieldsBank[selectedRow.bankName?.toUpperCase() as keyof typeof extraFieldsBank] || []
  const canSave = !hasChangedOffice || formValid
  const dispatch = useDispatch()

  console.log(isObservation, isTest)

  useEffect(() => {
    if (selectedRow.bankName) {
      setInitialExtraForm(extraEmptyForm)
    }
    if (isOpen) {
      setSelectedStages({
        office: selectedRow.office ? new Date(selectedRow.office) : null,
        studyDate: selectedRow.studyDate ? new Date(selectedRow.studyDate) : null,
        success: selectedRow.success ? new Date(selectedRow.success) : null,
        bank: selectedRow.bank ? new Date(selectedRow.bank) : null,
        fee: selectedRow.fee,
        payments: selectedRow.payments,
        delivered: selectedRow.delivered ? new Date(selectedRow.delivered) : null,
        rejected: selectedRow.rejected ? new Date(selectedRow.rejected) : null,
        received: selectedRow.received ? new Date(selectedRow.received) : null,
        returned: selectedRow.returned ? new Date(selectedRow.returned) : null
      })
      setSuccessForm(prev => ({
        ...prev,
        email: selectedRow.email || '',
        companyDocs: selectedRow.companyDocs || '',
        creditNumber: selectedRow.creditNumber || null,
        observations: selectedRow.observations || '',
        newInstallments: selectedRow.newInstallments || null,
        savedInstallments: selectedRow.savedInstallments || null,
        savedTaxes: selectedRow.savedTaxes || null,
        newTotalAmount: selectedRow.newTotalAmount || null
      }))
      setHasChangeOffice(!!selectedRow.office)
      setHasChangeBank(!!selectedRow.bank)
      setIsRejectedState(selectedRow.isRejected || false)
      setIsReturnedState(selectedRow.isReturned || false)
      setIsObservation(selectedRow.isObservation || false)
      setIsTest(selectedRow.isTest || false)
      setConversation(selectedRow.conversation || [])
    }
  }, [isOpen, selectedRow])

  const setExtraFormValues = (newFields: ExtraField[]) => {
    if (newFields.length <= 0) return
    const newExtraForm = extraForm.map(item => {
      const updatedField = newFields.find(field => field.name === item.name)
      return updatedField ? { ...item, value: updatedField.value } : item
    })
    setExtraForm(newExtraForm)
  }

  const setInitialExtraForm = (newFields: ExtraField[]) => {
    if (newFields.length <= 0) return
    const newExtraForm = newFields.map(item => {
      const updatedField = selectedRow?.extraFields?.find(field => field.name === item.name)
      return updatedField ? { ...item, value: updatedField.value } : item
    })
    setExtraForm(newExtraForm)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { studyDate, office, bank, success, fee, payments, delivered, rejected, received, returned } = selectedStages
    onSave(
      {
        studyDate: studyDate ? format(studyDate, 'yyyy/MM/dd') : '',
        office: office ? format(office, 'yyyy/MM/dd') : '',
        bank: bank ? format(bank, 'yyyy/MM/dd') : '',
        success: success ? format(success, 'yyyy/MM/dd') : '',
        fee,
        payments,
        delivered: delivered ? format(delivered, 'yyyy/MM/dd') : '',
        rejected: rejected ? format(rejected, 'yyyy/MM/dd') : '',
        received: received ? format(received, 'yyyy/MM/dd') : '',
        returned: returned ? format(returned, 'yyyy/MM/dd') : ''
      },
      extraForm,
      isRejectedState,
      isReturnedState,
      successForm,
      hasChangedSuccess
    )
    if (newConversation) {
      dispatch(
        postNewConversation({
          value: newConversation,
          userId: selectedRow._id,
          type: 'observation'
        })
      )
    }
  }

  const handleSuccess = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onSuccess()
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => {
          setSelectedStages(emptyStages)
          setSuccessForm(emptySuccessForm)
          setHasChangeSuccess(false)
          setHasChangeOffice(false)
          onCancel()
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'row'
        }}
        onSubmit={handleSubmit}
      >
        <form className="modal_main">
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <div className="modal_header">Seguimiento de caso: {selectedRow?.name}</div>
            <div className="modal_form_edit_stages">
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Box fontSize="18px" fontWeight="bold" margin="0 10px 10px 10px" color="#fc6404">
                    Editar etapas:
                  </Box>
                  <Box className="side_form">
                    <Grid container>
                      <Grid item xs={11}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          inputVariant="outlined"
                          label="Fecha de estudio"
                          placeholder="fecha"
                          emptyLabel=""
                          clearable
                          format="yyyy/MM/dd"
                          invalidDateMessage="Fecha incorrecta"
                          value={selectedStages.studyDate}
                          onChange={(e: Date | null) => {
                            setSelectedStages(prev => ({
                              ...prev,
                              studyDate: e
                            }))
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          style={{
                            color: '#ff6900',
                            width: '100%'
                          }}
                          onClick={() => {
                            setSelectedStages(prev => ({
                              ...prev,
                              studyDate: null
                            }))
                          }}
                        >
                          <DeleteIcon style={{ width: '20px' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <br />
                    <Grid container>
                      <Grid item xs={11}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          inputVariant="outlined"
                          label="Completo en oficina"
                          emptyLabel=""
                          clearable
                          placeholder="fecha"
                          format="yyyy/MM/dd"
                          invalidDateMessage="Fecha incorrecta"
                          value={selectedStages.office}
                          onChange={(e: Date | null) => {
                            setHasChangeOffice(true)
                            setSelectedStages(prev => ({
                              ...prev,
                              office: e
                            }))
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          style={{
                            color: '#ff6900',
                            width: '100%'
                          }}
                          onClick={() => {
                            setSelectedStages(prev => ({
                              ...prev,
                              office: null
                            }))
                            setHasChangeOffice(false)
                          }}
                        >
                          <DeleteIcon style={{ width: '20px' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <br />
                    {[Role.admin, Role.operaciones].includes(role) && hasChangedOffice ? (
                      <Grid container style={{ padding: '10px' }}>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Email destinatario"
                            variant="outlined"
                            margin="normal"
                            placeholder="email"
                            required
                            fullWidth
                            value={successForm.email}
                            onChange={e => {
                              setSuccessForm(prev => ({
                                ...prev,
                                email: e.target.value
                              }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Documentos escaneados"
                            variant="outlined"
                            margin="normal"
                            placeholder="URL"
                            required
                            fullWidth
                            value={successForm.companyDocs}
                            onChange={e => {
                              setSuccessForm(prev => ({
                                ...prev,
                                companyDocs: e.target.value
                              }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Numero de crédito"
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            value={successForm.creditNumber}
                            onChange={e => {
                              if (e.target.value === '' || numRegex.test(e.target.value))
                                setSuccessForm(prev => ({
                                  ...prev,
                                  creditNumber: Number(e.target.value)
                                }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Nuevo plazo"
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            value={successForm.newInstallments}
                            onChange={e => {
                              if (e.target.value === '' || numRegex.test(e.target.value))
                                setSuccessForm(prev => ({
                                  ...prev,
                                  newInstallments: Number(e.target.value)
                                }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Cuotas ahorradas"
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            value={successForm.savedInstallments}
                            onChange={e => {
                              if (e.target.value === '' || numRegex.test(e.target.value))
                                setSuccessForm(prev => ({
                                  ...prev,
                                  savedInstallments: Number(e.target.value)
                                }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Intereses ahorrados"
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            value={successForm.savedTaxes}
                            onChange={e => {
                              if (e.target.value === '' || numRegex.test(e.target.value))
                                setSuccessForm(prev => ({
                                  ...prev,
                                  savedTaxes: Number(e.target.value)
                                }))
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Valor nueva cuota"
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            value={successForm.newTotalAmount}
                            onChange={e => {
                              if (e.target.value === '' || numRegex.test(e.target.value))
                                setSuccessForm(prev => ({
                                  ...prev,
                                  newTotalAmount: Number(e.target.value)
                                }))
                            }}
                          />
                        </Grid>
                        {!!selectedRow.observations && (
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Observaciones"
                              variant="outlined"
                              margin="normal"
                              disabled
                              fullWidth
                              value={successForm.observations}
                            />
                          </Grid>
                        )}
                      </Grid>
                    ) : null}
                    <Grid container>
                      <Grid item xs={11}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          inputVariant="outlined"
                          label="Enviado a gestor"
                          emptyLabel=""
                          clearable
                          placeholder="fecha"
                          format="yyyy/MM/dd"
                          invalidDateMessage="Fecha incorrecta"
                          value={selectedStages.delivered}
                          onChange={(e: Date | null) => {
                            setHasChangeOffice(true)
                            setSelectedStages(prev => ({
                              ...prev,
                              delivered: e
                            }))
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          style={{
                            color: '#ff6900',
                            width: '100%'
                          }}
                          onClick={() => {
                            setSelectedStages(prev => ({
                              ...prev,
                              delivered: null
                            }))
                            setHasChangeOffice(false)
                          }}
                        >
                          <DeleteIcon style={{ width: '20px' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <br />
                    <TextField
                      size="small"
                      fullWidth
                      label="Honorarios $"
                      variant="outlined"
                      margin="normal"
                      value={selectedStages.fee}
                      onChange={e => {
                        if (e.target.value === '' || numRegex.test(e.target.value))
                          setSelectedStages(prev => ({
                            ...prev,
                            fee: Number(e.target.value)
                          }))
                      }}
                    />
                    <Grid container>
                      <Grid item xs={11}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          inputVariant="outlined"
                          label="Radicado en banco"
                          format="yyyy/MM/dd"
                          emptyLabel=""
                          placeholder="fecha"
                          clearable
                          invalidDateMessage="Fecha incorrecta"
                          value={selectedStages.bank}
                          onChange={(e: Date | null) => {
                            setSelectedStages(prev => ({
                              ...prev,
                              bank: e
                            }))
                            setHasChangeBank(true)
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          style={{
                            color: '#ff6900',
                            width: '100%'
                          }}
                          onClick={() => {
                            setSelectedStages(prev => ({
                              ...prev,
                              bank: null
                            }))
                            setHasChangeBank(false)
                          }}
                        >
                          <DeleteIcon style={{ width: '20px' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    {[Role.admin, Role.operaciones].includes(role) && hasChangedBank ? (
                      <Grid container style={{ padding: '10px' }}>
                        {extraForm.map(item => {
                          return item.type === 'date' ? (
                            <Grid item xs={11} key={item.name}>
                              <DatePicker
                                size="small"
                                style={{ width: '100%' }}
                                inputVariant="outlined"
                                label={item.name}
                                format="yyyy/MM/dd"
                                emptyLabel=""
                                margin="normal"
                                placeholder="fecha"
                                clearable
                                invalidDateMessage="Fecha incorrecta"
                                value={item.value || null}
                                onChange={(e: Date | null) => {
                                  setExtraFormValues([
                                    { name: item.name, type: item.type, value: e ? format(e, 'yyyy/MM/dd') : '' }
                                  ])
                                }}
                              />
                            </Grid>
                          ) : (
                            <Grid item xs={11} key={item.name}>
                              <TextField
                                size="small"
                                label={item.name}
                                style={{ width: '100%' }}
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                value={item.value}
                                onChange={e => {
                                  setExtraFormValues([{ name: item.name, type: item.type, value: e.target.value }])
                                }}
                              />
                            </Grid>
                          )
                        })}
                      </Grid>
                    ) : (
                      ''
                    )}
                    <br />
                    <Grid container>
                      <Grid item xs={11}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          inputVariant="outlined"
                          label="Exitoso"
                          emptyLabel=""
                          clearable
                          placeholder="fecha"
                          format="yyyy/MM/dd"
                          invalidDateMessage="Fecha incorrecta"
                          value={selectedStages.success}
                          onChange={(e: Date | null) => {
                            setHasChangeSuccess(true)
                            setSelectedStages(prev => ({
                              ...prev,
                              success: e
                            }))
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          style={{
                            color: '#ff6900',
                            width: '100%'
                          }}
                          onClick={() => {
                            setHasChangeSuccess(false)
                            setSelectedStages(prev => ({
                              ...prev,
                              success: null
                            }))
                          }}
                        >
                          <DeleteIcon style={{ width: '20px' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <TextField
                      size="small"
                      fullWidth
                      label="Abonos"
                      variant="outlined"
                      margin="normal"
                      value={selectedStages.payments}
                      onChange={e => {
                        if (e.target.value === '' || numRegex.test(e.target.value))
                          setSelectedStages(prev => ({
                            ...prev,
                            payments: Number(e.target.value)
                          }))
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} className="side_form">
                  <Box>
                    <Box fontSize="18px" fontWeight="bold" margin="0 0 10px 0" color="#fc6404">
                      Acciones:
                    </Box>
                    <Grid container direction="row">
                      <Grid item xs={6} md={4}>
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Typography
                            style={{
                              fontSize: '16px',
                              fontFamily: 'Roboto, sans-serif'
                            }}
                          >
                            Caso Devuelto:
                          </Typography>
                          <Switch
                            name="isReturned"
                            checked={isReturnedState}
                            onChange={e => setIsReturnedState(e.target.checked)}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Typography
                            style={{
                              fontSize: '16px',
                              fontFamily: 'Roboto, sans-serif'
                            }}
                          >
                            Caso Negado:
                          </Typography>
                          <Switch
                            name="isRejected"
                            checked={isRejectedState}
                            onChange={e => setIsRejectedState(e.target.checked)}
                          />
                        </Box>
                      </Grid>
                      {/* <Grid item xs={6} md={4}>
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Typography
                            style={{
                              fontSize: '16px',
                              fontFamily: 'Roboto, sans-serif'
                            }}
                          >
                            Caso Observación:
                          </Typography>
                          <Switch
                            name="isObservation"
                            checked={isObservation}
                            onChange={e => setIsObservation(e.target.checked)}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Typography
                            style={{
                              fontSize: '16px',
                              fontFamily: 'Roboto, sans-serif'
                            }}
                          >
                            Caso Prueba:
                          </Typography>
                          <Switch name="isTest" checked={isTest} onChange={e => setIsTest(e.target.checked)} />
                        </Box>
                      </Grid> */}
                      <Grid item xs={6} md={4}>
                        <Button onClick={handleSuccess} variant="contained" color="primary">
                          Enviar Carta de Éxito
                        </Button>
                      </Grid>
                    </Grid>
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-around"
                      margin="10px 0"
                    >
                      <DatePicker
                        size="small"
                        style={{ width: '100%', marginRight: '10px' }}
                        inputVariant="outlined"
                        label="Fecha devuelto"
                        placeholder="fecha"
                        emptyLabel=""
                        clearable
                        format="yyyy/MM/dd"
                        invalidDateMessage="Fecha devuelto"
                        value={selectedStages.returned}
                        onChange={(e: Date | null) => {
                          setSelectedStages(prev => ({
                            ...prev,
                            returned: e
                          }))
                        }}
                      />
                      <DatePicker
                        size="small"
                        style={{ width: '100%' }}
                        inputVariant="outlined"
                        label="Fecha de rechazo"
                        placeholder="fecha"
                        emptyLabel=""
                        clearable
                        format="yyyy/MM/dd"
                        invalidDateMessage="Fecha de rechazo"
                        value={selectedStages.rejected}
                        onChange={(e: Date | null) => {
                          setSelectedStages(prev => ({
                            ...prev,
                            rejected: e
                          }))
                        }}
                      />
                    </Box>
                    <Box fontSize="18px" fontWeight="bold" margin="10px 0 10px 0" color="#fc6404">
                      Observaciones:
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      padding="20px"
                      border="1px solid #ccc"
                      borderRadius="5px"
                      bgcolor="#fff"
                    >
                      {conversation
                        .filter(i => i.type !== 'executive') // TODO: replace by === observation migrate old comments to include type
                        .map(
                          (message, index) =>
                            message.value && (
                              <>
                                <Box
                                  key={index}
                                  display="flex"
                                  flexDirection="column"
                                  mb={1}
                                  p={1}
                                  bgcolor="#ffebd9"
                                  borderRadius="5px"
                                  boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
                                >
                                  <Box fontSize="0.6rem" fontWeight="bold" marginBottom="0.5rem">
                                    {message.authorName}:
                                  </Box>
                                  <Box whiteSpace="pre-line">{message.value}</Box>
                                  <Box
                                    fontSize="0.6rem"
                                    color="#fc6404"
                                    display="flex"
                                    justifyContent="flex-end"
                                    width="100%"
                                  >
                                    {format(new Date(message.date), 'yyyy/MM/dd')}
                                  </Box>
                                </Box>
                              </>
                            )
                        )}
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        label="Nueva Observación"
                        variant="outlined"
                        required={isRejectedState || isReturnedState}
                        margin="normal"
                        value={newConversation}
                        onChange={e => setNewConversation(e.target.value)}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </div>
            <div className="modal_footer">
              <Button
                className="footer_button"
                variant="contained"
                onClick={() => {
                  setSelectedStages(emptyStages)
                  setSuccessForm(emptySuccessForm)
                  setHasChangeSuccess(false)
                  onCancel()
                }}
              >
                Cancelar
              </Button>
              <Button className="footer_button" type="submit" variant="contained" color="primary" disabled={!canSave}>
                Guardar
              </Button>
            </div>
          </MuiPickersUtilsProvider>
        </form>
      </Modal>
    </>
  )
}

export default EditStagesModal
